import { useState, useMemo, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";
import { PageMeta } from "@/components/PageMeta";
import { canonicalUrl } from "@/lib/canonical";
import FieldHeader from "@/components/field/FieldHeader";
import FieldFooter from "@/components/field/FieldFooter";
import "@/styles/design-tokens.css";
import "./claims.css";

const DEFAULT_ANNUAL_CLAIMS = 50000;
const DEFAULT_AVG_CLAIM_VALUE = 150;
const DEFAULT_DENIAL_RATE = 10;

type DenialReason = { code: string; label: string; percentage: number; preventableRate: number; description: string; };

const DENIAL_REASONS: DenialReason[] = [
  { code: "CO-4", label: "Coding Error", percentage: 15, preventableRate: 85, description: "Procedure code inconsistent with modifier or not valid for date of service" },
  { code: "CO-11", label: "Not Covered", percentage: 20, preventableRate: 70, description: "Diagnosis inconsistent with procedure" },
  { code: "CO-97", label: "Already Adjudicated", percentage: 12, preventableRate: 95, description: "Payment adjusted because benefit for this service is included in another service" },
  { code: "OA-23", label: "Not Authorized", percentage: 8, preventableRate: 40, description: "Impact of prior authorization requirement" },
  { code: "CO-16", label: "Missing Information", percentage: 18, preventableRate: 80, description: "Claim lacks information needed for adjudication" },
  { code: "CO-151", label: "MUE Exceeded", percentage: 12, preventableRate: 95, description: "Units exceed medically unlikely edit limit" },
];

type CodeAnalysis = { code: string; description?: string; payment?: { non_facility: number; facility: number }; };

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
}
function formatNumber(num: number): string {
  return new Intl.NumberFormat("en-US").format(num);
}

function Field({ label, value, onChange, min, max, step, suffix, prefix, helpText }: {
  label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; suffix?: string; prefix?: string; helpText?: string;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      <div className="ipt">
        {prefix && <span className="pre">{prefix}</span>}
        <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} min={min} max={max} step={step} className={`${prefix ? "haspre" : ""} ${suffix ? "hassuf" : ""}`} />
        {suffix && <span className="suf">{suffix}</span>}
      </div>
      {helpText && <p className="help">{helpText}</p>}
    </div>
  );
}

function Metric({ label, value, sublabel, variant = "default" }: { label: string; value: string; sublabel?: string; variant?: "default" | "danger" | "success" | "brand"; }) {
  return (
    <div className={`metric ${variant}`}>
      <div className="l">{label}</div>
      <div className="v">{value}</div>
      {sublabel && <div className="s">{sublabel}</div>}
    </div>
  );
}

function ApiFeatureCard({ title, description, endpoint, preventsCodes }: { title: string; description: string; endpoint: string; preventsCodes: string[]; }) {
  return (
    <div className="pcard">
      <h3>{title}</h3>
      <p>{description}</p>
      <p className="ep">{endpoint}</p>
      <div className="codes">{preventsCodes.map((c) => <span key={c}>Prevents {c}</span>)}</div>
    </div>
  );
}

function SourceLink({ label, href }: { label: string; href: string }) {
  return <a href={href} target="_blank" rel="noopener noreferrer">{label} ↗</a>;
}

export default function ClaimsCalculatorPage() {
  const [annualClaims, setAnnualClaims] = useState(DEFAULT_ANNUAL_CLAIMS);
  const [avgClaimValue, setAvgClaimValue] = useState(DEFAULT_AVG_CLAIM_VALUE);
  const [denialRate, setDenialRate] = useState(DEFAULT_DENIAL_RATE);
  const [reasons, setReasons] = useState(DENIAL_REASONS);

  const hasTrackedUse = useRef(false);
  const trackFirstUse = useCallback(() => {
    if (hasTrackedUse.current) return;
    hasTrackedUse.current = true;
    trackEvent({ action: "calculator_used", category: "tools", label: "claims_denial" });
  }, []);

  const [cptCodes, setCptCodes] = useState("");
  const [codeAnalyses, setCodeAnalyses] = useState<CodeAnalysis[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const updateReasonPercentage = useCallback((index: number, newPct: number) => {
    setReasons((prev) => {
      const othersTotal = prev.reduce((sum, r, i) => (i === index ? sum : sum + r.percentage), 0);
      const capped = Math.min(newPct, 100 - othersTotal);
      const next = [...prev];
      next[index] = { ...next[index]!, percentage: Math.max(0, capped) };
      return next;
    });
  }, []);

  const results = useMemo(() => {
    const totalRevenue = annualClaims * avgClaimValue;
    const deniedClaims = annualClaims * (denialRate / 100);
    const totalDeniedRevenue = deniedClaims * avgClaimValue;
    let totalPreventable = 0;
    const reasonBreakdown = reasons.map((reason) => {
      const deniedByReason = deniedClaims * (reason.percentage / 100);
      const preventable = deniedByReason * (reason.preventableRate / 100);
      const preventableRevenue = preventable * avgClaimValue;
      totalPreventable += preventableRevenue;
      return { ...reason, deniedByReason: Math.round(deniedByReason), preventableClaims: Math.round(preventable), preventableRevenue };
    });
    const recoverableEstimate = totalPreventable * 0.6;
    return { totalRevenue, deniedClaims: Math.round(deniedClaims), totalDeniedRevenue, totalPreventable, recoverableEstimate, reasonBreakdown };
  }, [annualClaims, avgClaimValue, denialRate, reasons]);

  const analyzeCodes = useCallback(async () => {
    const codes = cptCodes.split(/[,\s]+/).map((c) => c.trim()).filter(Boolean);
    if (codes.length === 0) return;
    setIsAnalyzing(true);
    const analyses: CodeAnalysis[] = codes.map((code) => ({ code, description: `CPT ${code} — data available with FHIRfly API` }));
    setCodeAnalyses(analyses);
    setIsAnalyzing(false);
  }, [cptCodes]);

  const assigned = reasons.reduce((sum, r) => sum + r.percentage, 0);
  const other = Math.max(0, 100 - assigned);

  return (
    <div className="ffly ffly--public claims-page">
      <PageMeta
        title="Claims Denial Cost Calculator"
        description="Estimate how much revenue your organization loses to preventable claim denials and how much you could recover with real-time claims validation from FHIRfly."
        canonical={canonicalUrl("/claims-calculator")}
      />
      <FieldHeader active="tools" />

      <div className="wrap">
        <div className="masthead">
          <div className="eyebrow">Free tool · Claims</div>
          <h1>Claims denial cost calculator</h1>
          <p className="lede">Estimate the revenue you lose to preventable claim denials — and how much you could recover with pre-submission validation.</p>
        </div>

        <div className="layout">
          {/* Inputs */}
          <div className="stack">
            <div className="card">
              <h2>Your claims profile</h2>
              <Field label="Annual claims volume" value={annualClaims} onChange={(v) => { trackFirstUse(); setAnnualClaims(v); }} min={100} max={10000000} step={1000} helpText="Total claims submitted per year" />
              <Field label="Average claim value" value={avgClaimValue} onChange={(v) => { trackFirstUse(); setAvgClaimValue(v); }} min={10} max={10000} step={10} prefix="$" helpText="Mean reimbursement per claim" />
              <Field label="Denial rate" value={denialRate} onChange={(v) => { trackFirstUse(); setDenialRate(v); }} min={1} max={50} step={0.5} suffix="%" helpText="Industry average: 5–15%" />
              <div className="mix">
                <h3>Denial reason mix</h3>
                {reasons.map((reason, i) => (
                  <div className="srow" key={reason.code}>
                    <span className="code">{reason.code}</span>
                    <input type="range" min={0} max={40} value={reason.percentage} onChange={(e) => { trackFirstUse(); updateReasonPercentage(i, Number(e.target.value)); }} />
                    <span className="pct">{reason.percentage}%</span>
                  </div>
                ))}
                <div className="srow other">
                  <span className="code">Other</span>
                  <div className="bar" />
                  <span className="pct">{other}%</span>
                </div>
              </div>
            </div>

            <div className="card">
              <h2>Code-level analysis<span className="opt">Optional</span></h2>
              <p className="help" style={{ marginBottom: 12 }}>Enter your top billed CPT/HCPCS codes for specific NCCI edit, MUE limit, and payment data from CMS.</p>
              <textarea value={cptCodes} onChange={(e) => setCptCodes(e.target.value)} placeholder="99213, 99214, 99215, 36415, 80053" rows={2} />
              <button className="analyze" onClick={analyzeCodes} disabled={isAnalyzing || !cptCodes.trim()}>{isAnalyzing ? "Analyzing…" : "Analyze my codes"}</button>
              {codeAnalyses.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  {codeAnalyses.map((a) => (
                    <div className="codeline" key={a.code}><div className="c">CPT {a.code}</div><div className="d">{a.description}</div></div>
                  ))}
                  <p className="codenote">Full code-level analysis available with the FHIRfly API.</p>
                </div>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="stack">
            <div className="metrics">
              <Metric label="Total annual revenue" value={formatCurrency(results.totalRevenue)} sublabel={`${formatNumber(annualClaims)} claims at ${formatCurrency(avgClaimValue)} avg`} />
              <Metric label="Revenue lost to denials" value={formatCurrency(results.totalDeniedRevenue)} sublabel={`${formatNumber(results.deniedClaims)} denied claims (${denialRate}%)`} variant="danger" />
              <Metric label="Preventable denial loss" value={formatCurrency(results.totalPreventable)} sublabel="Addressable with pre-submission validation" variant="brand" />
              <Metric label="Estimated recoverable" value={formatCurrency(results.recoverableEstimate)} sublabel="Conservative 60% recovery rate" variant="success" />
            </div>

            <div className="card">
              <h2>Denial breakdown by reason</h2>
              <div style={{ overflowX: "auto" }}>
                <table className="tbl">
                  <thead>
                    <tr><th className="l">Code</th><th className="l">Reason</th><th className="r">Denied</th><th className="r">Preventable</th><th className="r">$ Recoverable</th></tr>
                  </thead>
                  <tbody>
                    {results.reasonBreakdown.map((row) => (
                      <tr key={row.code}>
                        <td className="l mono">{row.code}</td>
                        <td className="l">{row.label}</td>
                        <td className="r">{formatNumber(row.deniedByReason)}</td>
                        <td className="r prev">{formatNumber(row.preventableClaims)}</td>
                        <td className="r rec">{formatCurrency(row.preventableRevenue * 0.6)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* How FHIRfly prevents denials */}
        <div className="prevent">
          <h1 style={{ fontSize: 28, textAlign: "left", margin: "0 0 0" }}>How FHIRfly prevents denials</h1>
          <p>Most claim denials are preventable with the right data at the right time. Four API endpoints catch common denial reasons before claims leave your billing system — turning reactive rework into proactive validation.</p>
          <div className="pgrid">
            <ApiFeatureCard title="NCCI PTP Validation" endpoint="GET /v1/ncci/validate" preventsCodes={["CO-4", "CO-97"]} description="Query the CMS National Correct Coding Initiative procedure-to-procedure edits in real time. Before submitting a claim with multiple CPT codes, check whether code pairs are allowed, require a modifier, or are mutually exclusive — catching the most common coding denials before they reach the payer." />
            <ApiFeatureCard title="MUE Limits" endpoint="GET /v1/mue/:hcpcs" preventsCodes={["CO-151"]} description="Medically Unlikely Edits cap the units of service per patient per day. FHIRfly returns the current MUE value and rationale for any CPT/HCPCS code, so your billing system can flag over-coded claims before submission." />
            <ApiFeatureCard title="PFS / RVU Data" endpoint="GET /v1/pfs/:hcpcs" preventsCodes={["CO-16"]} description="Access the Medicare Physician Fee Schedule with work, practice-expense, and malpractice RVU components. Forecast reimbursement, find undercoded services, and validate that claims are complete before submission." />
            <ApiFeatureCard title="Coverage Check" endpoint="GET /v1/coverage/check" preventsCodes={["CO-11"]} description="Verify that an ICD-10 diagnosis supports the billed CPT procedure per Medicare LCD/NCD. Coverage mismatches are the single largest category of denials — pre-submission validation eliminates them." />
          </div>

          <div className="mcp">
            <i className="ico fa-solid fa-robot" />
            <div>
              <h3>AI agent integration via MCP</h3>
              <p>Your AI coding assistant (Claude, GPT, etc.) can query all of these APIs in real time via FHIRfly's MCP server — automatically evaluating every claim before submission. No manual lookups.</p>
              <Link to="/docs/mcp/overview">Learn about MCP →</Link>
            </div>
          </div>
        </div>

        <div className="sources">
          <h2>CMS data sources</h2>
          <div className="links">
            <SourceLink label="NCCI Edits" href="https://www.cms.gov/medicare/coding-billing/national-correct-coding-initiative-ncci-edits/medicare-ncci-procedure-procedure-ptp-edits" />
            <SourceLink label="MUE Values" href="https://www.cms.gov/medicare/coding-billing/national-correct-coding-initiative-ncci-edits/medicare-ncci-medically-unlikely-edits-mues" />
            <SourceLink label="Physician Fee Schedule" href="https://www.cms.gov/medicare/physician-fee-schedule/search" />
            <SourceLink label="Coverage Database" href="https://www.cms.gov/medicare-coverage-database" />
          </div>
        </div>
      </div>

      <FieldFooter variant="full" />
    </div>
  );
}
